/**
 * The message bus between this browser's Velora windows (main tabs and
 * pop-outs): one BroadcastChannel, same origin only, memory only. Messages
 * carry the selected patient's id and the label the selecting screen had
 * already shown (as the in-tab store does, patient/current.ts); nothing here
 * is written to storage or leaves the browser. Live data still arrives over
 * each window's own server-sent events (live/LiveProvider.tsx).
 */
import type { CurrentPatient } from '../patient/current.js';

export const WINDOW_CHANNEL = 'velora-windows';

export type WindowRole = 'main' | 'popout';

export type WindowMessage =
  | {
      t: 'patient';
      from: string;
      role: WindowRole;
      patient: CurrentPatient | null;
    }
  /** A pop-out announcing itself (on open, on navigation, and in answer to `who`). */
  | { t: 'hello'; from: string; key: string; label: string; pinned: boolean }
  | { t: 'bye'; from: string }
  /** Asks every pop-out to say hello (a main window counting open pop-outs). */
  | { t: 'who'; from: string }
  /** Asks every pop-out to close (main window: Close all pop-outs). */
  | { t: 'close-all'; from: string };

function makeId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `w${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }
}

/** This window's id for the bus (per page load). */
export const WINDOW_ID = makeId();

let channel: BroadcastChannel | null | undefined;
const listeners = new Set<(m: WindowMessage) => void>();

function open(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  if (typeof BroadcastChannel === 'undefined') {
    channel = null;
    return null;
  }
  try {
    channel = new BroadcastChannel(WINDOW_CHANNEL);
    channel.onmessage = (ev: MessageEvent<unknown>) => {
      const m = ev.data as WindowMessage | null;
      if (!m || typeof m !== 'object' || typeof m.t !== 'string' || m.from === WINDOW_ID) return;
      for (const l of [...listeners]) l(m);
    };
  } catch {
    channel = null;
  }
  return channel;
}

/** Sends to every other Velora window of this browser (a no-op without BroadcastChannel). */
export function postWindowMessage(message: WindowMessage): void {
  try {
    open()?.postMessage(message);
  } catch {
    // A closing window or an uncloneable value: the other windows simply miss one update.
  }
}

export function onWindowMessage(listener: (m: WindowMessage) => void): () => void {
  open();
  listeners.add(listener);
  return () => listeners.delete(listener);
}
